import { CalendarOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import type { IntlShape } from "@formatjs/intl";
import type { Dispatch } from "@reduxjs/toolkit";
import { Button, Flex, Table, Tag, Typography } from "antd";
import type { HookAPI } from "antd/es/modal/useModal";
import type { ColumnType } from "antd/es/table";
import { useCallback, useMemo, useState, type Key } from "react";
import { actions as routerActions } from "redux-router5";
import { actionsValue } from "../../common/columns";
import { ActiveCheckInBadge } from "../../common/components/ActiveCheckInBadge";
import { CompletionProgress } from "../../common/components/CompletionProgress";
import { EditableDatePicker } from "../../common/components/EditableDatePicker";
import { NoData } from "../../common/components/NoDataComp";
import { ReportDoneBadge } from "../../common/components/ReportDoneBadge";
import { formatTotalTime } from "../../common/utils/formatTotalTime";
import { projectActions } from "../../features/project/projectSlice";
import { ScheduleTechniciansModal } from "../assignment-schedule/ScheduleTechniciansModal";
import { routeNames } from "../common/routes";
import type {
  CurrentUser,
  Services,
  Ticket,
  TicketInProject,
  Turbine,
  UserLimitedList,
} from "../generated-axios-client";
import { EditableAssignees } from "../ticket/EditableAssignees";
import { statusColorMap, statusLabelMap } from "../ticket/ticketConstants";
import { DragHandle, SortableDragRow } from "./SortableDragRow";
import { useDragSort } from "./useDragSort";

type Props = {
  tickets: TicketInProject[];
  projectId: string;
  intl: IntlShape;
  dispatch: Dispatch;
  serviceObjects: Record<string, Services> | null;
  turbines: Turbine[] | undefined;
  technicians: UserLimitedList[];
  user: CurrentUser | undefined;
  modalApi: HookAPI | undefined;
};

const buildEditableDateColumn = (
  field: "start" | "deadline",
  titleMessageId: string,
  intl: IntlShape,
  dispatch: Dispatch,
  projectId: string
): ColumnType => ({
  key: field,
  dataIndex: field,
  title: intl.formatMessage({ id: titleMessageId }),
  render: (value: string | null, record: unknown) => {
    const ticketId = (record as TicketInProject).id;
    return (
      <EditableDatePicker
        variableName={field}
        variableValue={value}
        intl={intl}
        onChange={
          ticketId
            ? (_variableName, isoValue) =>
                dispatch(
                  projectActions.projectTicketDateEdit({
                    projectId,
                    ticketId,
                    field,
                    value: isoValue,
                  })
                )
            : undefined
        }
      />
    );
  },
});

const buildColumns = (
  intl: IntlShape,
  serviceObjects: Record<string, Services> | null,
  turbinesById: Record<string, Turbine>,
  technicians: UserLimitedList[],
  savingId: string | null,
  dispatch: Dispatch,
  projectId: string
): ColumnType[] => [
  {
    key: "drag",
    dataIndex: "drag",
    title: "",
    width: 40,
    render: (_: unknown, record: unknown) => {
      const ticket = record as TicketInProject;
      return <DragHandle saving={savingId === ticket.id} />;
    },
  },
  {
    key: "priority_position",
    title: intl.formatMessage({ id: "table_column_priority_position" }),
    width: 80,
    align: "center",
    render: (_: unknown, _record: unknown, rowIndex: number) => (
      <Typography.Text strong>{rowIndex + 1}</Typography.Text>
    ),
  },
  {
    key: "ticket_number",
    dataIndex: "ticket_number",
    title: intl.formatMessage({ id: "table_column_ticket_number" }),
    render: (value: number | null, record: unknown) => {
      if (value == null) return <NoData intl={intl} />;
      const ticket = record as TicketInProject;
      return ticket.id ? (
        <Typography.Link
          strong
          onClick={() =>
            dispatch(
              routerActions.navigateTo(routeNames.TICKET_VIEW, {
                id: ticket.id,
              })
            )
          }
        >
          {`#${value}`}
        </Typography.Link>
      ) : (
        <Typography.Text strong type="secondary">
          {`#${value}`}
        </Typography.Text>
      );
    },
  },
  {
    key: "service",
    dataIndex: "service",
    title: intl.formatMessage({ id: "table_column_service" }),
    render: (value: string | null) => {
      const label = value ? serviceObjects?.[value]?.name : null;
      return label ?? <NoData intl={intl} />;
    },
  },
  {
    key: "report",
    dataIndex: "report_done",
    title: intl.formatMessage({ id: "table_column_report" }),
    width: 130,
    render: (value: boolean | null | undefined) => <ReportDoneBadge reportDone={value} />,
  },
  {
    key: "assignee",
    dataIndex: "assignees",
    title: intl.formatMessage({ id: "table_column_assignee" }, { count: 1 }),
    width: 160,
    render: (assignees: TicketInProject["assignees"], record: unknown) => {
      const ticketId = (record as TicketInProject).id;
      return (
        <EditableAssignees
          intl={intl}
          assignees={(assignees ?? []).map((a) => a.assignee)}
          technicians={technicians}
          onChange={
            ticketId
              ? (assigneeIds) =>
                  dispatch(
                    projectActions.projectTicketAssigneesEdit({
                      projectId,
                      ticketId,
                      assigneeIds,
                    })
                  )
              : undefined
          }
        />
      );
    },
  },
  {
    key: "turbine",
    dataIndex: "turbine",
    title: intl.formatMessage({ id: "table_column_turbine" }),
    render: (value: string | null) => {
      const serialNumber = value ? turbinesById[value]?.serial_number : null;
      if (!value || !serialNumber) return <NoData intl={intl} />;
      return (
        <Typography.Link
          onClick={() =>
            dispatch(routerActions.navigateTo(routeNames.TURBINE_VIEW, { id: value }))
          }
        >
          {serialNumber}
        </Typography.Link>
      );
    },
  },
  {
    key: "park_number",
    dataIndex: "turbine",
    title: intl.formatMessage({ id: "table_column_park_number" }),
    render: (value: string | null) => {
      const parkNumber = value ? turbinesById[value]?.park_number : null;
      return parkNumber || <NoData intl={intl} />;
    },
  },
  {
    key: "location",
    dataIndex: "location",
    title: intl.formatMessage({ id: "table_column_location" }),
    render: (value: string | null, record: unknown) => {
      const siteName = (record as TicketInProject).location_name;
      if (!value || !siteName) return <NoData intl={intl} />;
      return (
        <Typography.Link
          onClick={() =>
            dispatch(routerActions.navigateTo(routeNames.LOCATION_VIEW, { id: value }))
          }
        >
          {siteName}
        </Typography.Link>
      );
    },
  },
  {
    key: "progress",
    dataIndex: "items",
    title: intl.formatMessage({ id: "table_column_progress" }),
    width: 150,
    render: (items: TicketInProject["items"]) => (
      <CompletionProgress
        completedCount={items.filter((task) => task.completed === true).length}
        totalCount={items.length}
        intl={intl}
      />
    ),
  },
  buildEditableDateColumn("start", "table_column_start", intl, dispatch, projectId),
  buildEditableDateColumn("deadline", "table_column_deadline", intl, dispatch, projectId),
  {
    key: "active_checkin",
    dataIndex: "active_time_tracking",
    title: intl.formatMessage({ id: "table_column_active_checkin" }),
    width: 130,
    render: (value: number) => <ActiveCheckInBadge activeTimeTracking={value} />,
  },
  {
    key: "total_time",
    dataIndex: "total_time",
    title: intl.formatMessage({ id: "table_column_total_time" }),
    width: 110,
    render: (value: number | null) => (
      <Typography.Text type={value != null ? undefined : "secondary"}>
        {formatTotalTime(value)}
      </Typography.Text>
    ),
  },
  {
    key: "status",
    dataIndex: "status",
    title: intl.formatMessage({ id: "table_column_status" }),
    render: (value: string | null) =>
      value && value in statusLabelMap ? (
        <Tag
          color={value in statusColorMap ? statusColorMap[value] : "gray"}
          variant="outlined"
          styles={{ root: { fontWeight: 700 } }}
        >
          {statusLabelMap[value]}
        </Tag>
      ) : (
        <NoData intl={intl} />
      ),
  },
  {
    key: "actions",
    title: intl.formatMessage({ id: "table_column_actions" }),
    width: 80,
    render: () => null,
  },
];

export const ProjectWorkOrdersTable = ({
  tickets,
  projectId,
  intl,
  dispatch,
  serviceObjects,
  turbines,
  technicians,
  user,
  modalApi,
}: Props) => {
  const { sortedTickets, savingId, onDragEnd } = useDragSort(
    tickets,
    projectId,
    dispatch
  );

  // several work orders of the project can be booked for the same technicians at once
  const [selectedTicketIds, setSelectedTicketIds] = useState<Key[]>([]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const selectedWorkOrders = useMemo(
    () =>
      sortedTickets
        .filter((ticket) => ticket.id && selectedTicketIds.includes(ticket.id))
        .map((ticket) => ({
          id: ticket.id as string,
          ticketNumber: ticket.ticket_number,
        })),
    [sortedTickets, selectedTicketIds]
  );
  const closeBooking = useCallback(() => {
    setBookingOpen(false);
    setSelectedTicketIds([]);
  }, []);

  const turbinesById: Record<string, Turbine> = turbines
    ? Object.fromEntries(turbines.map((t) => [t.id, t]))
    : {};

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const columns = buildColumns(
    intl,
    serviceObjects,
    turbinesById,
    technicians,
    savingId,
    dispatch,
    projectId
  ).map((col) => {
    if (col.key === "actions") {
      return {
        ...col,
        render: (_: unknown, record: unknown) => {
          const ticket = record as TicketInProject;
          const ticketId = ticket.id;
          if (!ticketId) return null;
          const confirmDelete = () =>
            modalApi?.confirm({
              title: intl.formatMessage({ id: "ticket_delete_confirmation" }),
              icon: <ExclamationCircleFilled />,
              okText: intl.formatMessage({ id: "confirm" }),
              okType: "danger",
              cancelText: intl.formatMessage({ id: "cancel" }),
              onOk() {
                dispatch(projectActions.projectTicketDelete({ projectId, ticketId }));
              },
            });
          return actionsValue(
            undefined,
            ticket as unknown as Ticket,
            undefined,
            user,
            () =>
              dispatch(
                routerActions.navigateTo(routeNames.TICKET_VIEW, { id: ticketId })
              ),
            () =>
              dispatch(
                routerActions.navigateTo(routeNames.TICKET_EDIT, { id: ticketId })
              ),
            confirmDelete,
            undefined,
            "Work Order"
          );
        },
      };
    }
    return col;
  });

  return (
    <Flex vertical gap="small">
      <Flex justify="flex-end">
        <Button
          icon={<CalendarOutlined />}
          disabled={selectedWorkOrders.length === 0}
          onClick={() => setBookingOpen(true)}
        >
          {intl.formatMessage(
            { id: "schedule_technicians_selected" },
            { count: selectedWorkOrders.length }
          )}
        </Button>
      </Flex>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext
          items={sortedTickets.map((t) => t.id ?? "")}
          strategy={verticalListSortingStrategy}
        >
          <Table
            columns={columns}
            dataSource={sortedTickets}
            rowKey={(record) => (record as TicketInProject).id ?? ""}
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: "No work orders for this project" }}
            size="small"
            components={{ body: { row: SortableDragRow } }}
            rowSelection={{
              selectedRowKeys: selectedTicketIds,
              onChange: setSelectedTicketIds,
              getCheckboxProps: (record) => ({
                disabled: (record as TicketInProject).status === "DONE",
              }),
            }}
          />
        </SortableContext>
      </DndContext>
      <ScheduleTechniciansModal
        open={bookingOpen}
        intl={intl}
        workOrders={selectedWorkOrders}
        source={{ projectId }}
        onClose={closeBooking}
      />
    </Flex>
  );
};
